import { useState, useRef, useCallback } from "react";
import { useDistrictAccess } from "./useDistrictAccess";
import { useSchoolAccess } from "./useSchoolAccess";
import { MODAL_MODES, SCOPE } from "../constants";

export function useAccessControl(scope) {
  const isDistrict = scope === SCOPE.DISTRICT;

  const district = useDistrictAccess();
  const school = useSchoolAccess();

  const hook = isDistrict ? district : school;

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState(MODAL_MODES.CREATE);
  const [historyOpen, setHistoryOpen] = useState(false);

  const formResetRef = useRef(null);

  const registerFormReset = useCallback((fn) => {
    formResetRef.current = fn;
  }, []);

  const openCreateModal = useCallback(() => {
    formResetRef.current?.({
      startDate: undefined,
      endDate: undefined,
      reason: "",
    });
    setModalMode(MODAL_MODES.CREATE);
    setModalOpen(true);
  }, []);

  const openEditModal = useCallback(() => {
    const r = hook.activeRestriction;
    formResetRef.current?.({
      startDate: r?.start ? new Date(r.start) : undefined,
      endDate: r?.end ? new Date(r.end) : undefined,
      reason: r?.reason ?? "",
    });
    setModalMode(MODAL_MODES.EDIT);
    setModalOpen(true);
  }, [hook.activeRestriction]);

  const closeModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  const handleToggle = useCallback(() => {
    if (hook.activeRestriction) {
      hook.revoke({ revokeReason: "Manually ended via toggle switch" });
    } else {
      openCreateModal();
    }
  }, [hook, openCreateModal]);

  const handleSave = useCallback(
    (formData) => {
      const startDate =
        formData.startDate instanceof Date
          ? formData.startDate
          : new Date(formData.startDate);

      const endDate =
        formData.endDate instanceof Date
          ? formData.endDate
          : new Date(formData.endDate);

      const endOfDay = new Date(endDate);
      endOfDay.setHours(23, 59, 59, 999);

      const dto = {
        reason: formData.reason,
        startsAt: startDate.toISOString(),
        endsAt: endOfDay.toISOString(),
      };

      const mutation =
        modalMode === MODAL_MODES.EDIT ? hook.update : hook.create;
      mutation(dto, {
        onSuccess: closeModal,
        onError: (err) => {},
      });
    },
    [modalMode, hook, closeModal],
  );

  const handleRevoke = useCallback(() => {
    hook.revoke({ revokeReason: "Manually ended by user" });
  }, [hook]);

  const modalError =
    modalMode === MODAL_MODES.EDIT ? hook.updateError : hook.createError;

  return {
    isRestricted: !!hook.activeRestriction,
    restriction: hook.activeRestriction,
    districtRestriction: isDistrict ? null : school.districtRestriction,
    isLockedByDistrict: !isDistrict && !!school.districtRestriction,
    auditHistory: hook.auditHistory,

    isLoading: hook.isLoading,
    isError: hook.isError,
    isCreating: hook.isCreating || hook.isUpdating,
    isRevoking: hook.isRevoking,

    modalOpen,
    modalMode,
    modalError,
    revokeError: hook.revokeError,
    historyOpen,

    handleToggle,
    handleSave,
    handleRevoke,
    openEditModal,
    openCreateModal,
    closeModal,
    registerFormReset,
    toggleHistory: () => setHistoryOpen((o) => !o),
  };
}
